import type { StateCreator } from 'zustand';
import type { Money, Place, PlaceDetails } from '@my-fullstack-app/itinerary-types';
import { addMinutes, generateId, nextDayStartTime, placeFromDetails, sortByStartTime } from '../helpers';
import type { ItineraryState } from '../itinerary-store';
import { markTripDirty } from '../sync-bridge';
import { track } from '../../lib/analytics';

/**
 * CRUD for `Trip.wishlist[]` — candidate places saved while exploring
 * (the Discover tab) before they're committed to a day's schedule.
 * Wishlist entries are `Place` objects with no times; promoting one into
 * a day (`moveWishlistItemToDay`) hands it to `addPlace`, which derives
 * the start/end slot. Marks 'wishlist' dirty (its own wire field,
 * separate from 'days').
 */
export interface WishlistSlice {
  /** Returns true if the place was added, false if a same-`placeId`
   *  entry already existed (so the UI can toast "already saved"). */
  addWishlistItem: (tripId: string, details: PlaceDetails) => boolean;
  removeWishlistItem: (tripId: string, placeId: string) => void;
  moveWishlistItemToDay: (
    tripId: string,
    placeId: string,
    dayId: string,
    startTime?: string | undefined,
  ) => void;
  /** Set or clear the single estimated cost on a wishlist idea. Stored
   *  as one un-attributed `CostLineItem` on the place's `costs` (read via
   *  `getEntityCost`). Pass null/undefined to clear. */
  setWishlistItemCost: (tripId: string, placeId: string, cost: Money | null | undefined) => void;
}

export const createWishlistSlice: StateCreator<ItineraryState, [], [], WishlistSlice> = (set, get) => ({
  addWishlistItem: (tripId, details) => {
    const trip = get().trips.find((t) => t.id === tripId);
    if (!trip) return false;
    // De-dupe by source id: adding the same Google place twice would
    // render overlapping gold pins + duplicate cards with no benefit.
    const already = (trip.wishlist ?? []).some(
      (p) => details.placeId && p.placeId === details.placeId,
    );
    if (already) return false;
    get()._capture();
    const place = placeFromDetails(details);
    set((s) => ({
      trips: s.trips.map((t) =>
        t.id === tripId ? { ...t, wishlist: [...(t.wishlist ?? []), place] } : t,
      ),
    }));
    markTripDirty(tripId, ['wishlist']);
    track('wishlist.added', {
      tripId,
      hasPhoto: !!details.photoRef,
      hasCoords: details.lat != null && details.lng != null,
    });
    return true;
  },

  removeWishlistItem: (tripId, placeId) => {
    const trip = get().trips.find((t) => t.id === tripId);
    if (!trip?.wishlist?.some((p) => p.id === placeId)) return;
    get()._capture();
    set((s) => ({
      trips: s.trips.map((t) =>
        t.id === tripId ? { ...t, wishlist: (t.wishlist ?? []).filter((p) => p.id !== placeId) } : t,
      ),
    }));
    markTripDirty(tripId, ['wishlist']);
  },

  moveWishlistItemToDay: (tripId, placeId, dayId, startTime) => {
    const trip = get().trips.find((t) => t.id === tripId);
    const wishlistPlace = trip?.wishlist?.find((p) => p.id === placeId);
    const day = trip?.days.find((d) => d.id === dayId);
    if (!wishlistPlace || !day) return;
    get()._capture();
    // Move the FULL Place (preserve id + every field — customLabel,
    // notes linkage, costs, references) rather than round-tripping
    // through PlaceDetails, which would silently drop them. We only add
    // the day-scoped times here, mirroring addPlace's slotting via the
    // shared nextDayStartTime helper.
    const start = startTime ?? nextDayStartTime(day);
    const scheduled: Place = { ...wishlistPlace, startTime: start, endTime: addMinutes(start, 60) };
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return {
          ...t,
          wishlist: (t.wishlist ?? []).filter((p) => p.id !== placeId),
          days: t.days.map((d) =>
            d.id === dayId ? { ...d, places: sortByStartTime([...d.places, scheduled]) } : d,
          ),
        };
      }),
    }));
    markTripDirty(tripId, ['days', 'wishlist']);
    track('wishlist.moved_to_day', { tripId, dayId });
  },

  setWishlistItemCost: (tripId, placeId, cost) => {
    const trip = get().trips.find((t) => t.id === tripId);
    const place = trip?.wishlist?.find((p) => p.id === placeId);
    if (!place) return;
    // Reuse an existing line-item id when present so optimistic updates
    // target a stable row; otherwise mint one.
    const existingId = place.costs?.[0]?.id;
    const costs =
      cost && Number.isFinite(cost.amount) && cost.amount > 0 && cost.currency
        ? [{ id: existingId ?? generateId(), amount: cost.amount, currency: cost.currency }]
        : undefined;
    get()._capture();
    set((s) => ({
      trips: s.trips.map((t) =>
        t.id === tripId
          ? {
              ...t,
              wishlist: (t.wishlist ?? []).map((p) =>
                p.id === placeId ? { ...p, costs } : p,
              ),
            }
          : t,
      ),
    }));
    markTripDirty(tripId, ['wishlist']);
  },
});
